import * as React from "react";
import { GetSnapshotEventGroupedResponseItem } from "@/modules/snapshots/businessReports.types";
import { ChartGridLines, StackedStatusBar } from "@/modules/snapshots/snapshotEvents/components/stackedStatusBar";
import { useBarTooltip } from "@/modules/snapshots/snapshotEvents/components/useBarTooltip";

interface MonthItem {
  month: string;
  date: Date;
  total: number;
  done: number;
  inProgress: number;
  overdue: number;
}
interface Props {
  groupedEvents: Record<string, Array<GetSnapshotEventGroupedResponseItem>>;
  getStatusColor: (status: string) => string;
}

export const MonthlyTrendChart: React.FC<Props> = ({ getStatusColor, groupedEvents }) => {
  const { hoveredIndex: hoveredPoint, tooltipPosition, handleMouseEnter, handleMouseLeave } = useBarTooltip();
  const events = Object.values(groupedEvents).flat();

  // Group events by month and status
  const monthlyData = events.reduce<Record<string, MonthItem>>((acc, event) => {
    const date = new Date(event.event_date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

    if (!acc[monthKey]) {
      acc[monthKey] = {
        month: monthKey,
        date: new Date(date.getFullYear(), date.getMonth(), 1),
        total: 0,
        done: 0,
        inProgress: 0,
        overdue: 0,
      };
    }

    acc[monthKey].total++;
    switch (event.status.toLowerCase()) {
      case "done":
        acc[monthKey].done++;
        break;
      case "in_progress":
        acc[monthKey].inProgress++;
        break;
      case "overdue":
        acc[monthKey].overdue++;
        break;
    }

    return acc;
  }, {});

  const sortedMonthlyData = Object.values(monthlyData)
    .sort((a, b) => +a.date - +b.date)
    .slice(-12);

  // Chart dimensions
  const chartWidth = 400;
  const chartHeight = 240;
  const margin = { top: 15, right: 15, bottom: 15, left: 35 };
  const innerWidth = chartWidth - margin.left - margin.right;
  const innerHeight = chartHeight - margin.top - margin.bottom;

  const maxValue = Math.max(...sortedMonthlyData.map((d: { total: number }) => d.total), 1);
  const barWidth = (innerWidth / sortedMonthlyData.length) * 0.5;
  const barSpacing = innerWidth / sortedMonthlyData.length;
  const xScale = (index: number) => margin.left + index * barSpacing + (barSpacing - barWidth) / 2;

  return (
    <div className="relative flex min-h-[280px] w-full flex-col rounded-lg border border-[#e9e0d5] bg-white p-4 shadow-sm">
      {/* Header */}
      <div className="mb-4 text-center">
        <h3 className="m-0 text-lg font-semibold text-[#0a213e]">Monthly Trend</h3>
      </div>

      {/* Chart Container */}
      <div className="relative flex justify-center">
        <svg width={chartWidth} height={chartHeight} className="overflow-visible">
          <ChartGridLines margin={margin} innerWidth={innerWidth} innerHeight={innerHeight} />

          {/* Stacked bars */}
          {sortedMonthlyData.map((data, index) => (
            <StackedStatusBar
              key={`bar-${index}`}
              x={xScale(index)}
              baseY={margin.top + innerHeight}
              barWidth={barWidth}
              totalBarHeight={(data.total / maxValue) * innerHeight}
              total={data.total}
              done={data.done}
              inProgress={data.inProgress}
              overdue={data.overdue}
              isHovered={hoveredPoint === index}
              getStatusColor={getStatusColor}
              onMouseEnter={e => handleMouseEnter(e, index)}
              onMouseLeave={handleMouseLeave}
              renderLabel={topY =>
                data.total > 0 && (
                  <text
                    x={xScale(index) + barWidth / 2}
                    y={topY - 5}
                    textAnchor="middle"
                    className="fill-[#0a213e] text-[10px] font-semibold"
                  >
                    {data.total}
                  </text>
                )
              }
            />
          ))}
        </svg>

        {/* Tooltip */}
        {hoveredPoint !== null && (
          <div
            className="pointer-events-none absolute z-[1000] min-w-[180px] whitespace-nowrap rounded-lg border border-[#e9e0d5] bg-white p-4 text-sm shadow-lg"
            style={{
              left: tooltipPosition.x,
              top: tooltipPosition.y,
              fontSize: "14px",
            }}
          >
            <div className="mb-3 text-base font-semibold text-[#0a213e]">
              {sortedMonthlyData[hoveredPoint].date.toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
              })}
            </div>
            <div className="mb-2 text-[15px] text-[#3c3c41]">Total: {sortedMonthlyData[hoveredPoint].total} events</div>
            <div className="text-[14px] text-muted-foreground">
              <div className="mb-1 flex items-center gap-2">
                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: getStatusColor("done") }} />
                Done: {sortedMonthlyData[hoveredPoint].done}
              </div>
              <div className="mb-1 flex items-center gap-2">
                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: getStatusColor("in_progress") }} />
                In-Progress: {sortedMonthlyData[hoveredPoint].inProgress}
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: getStatusColor("overdue") }} />
                Overdue: {sortedMonthlyData[hoveredPoint].overdue}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
